import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Modal, Button, CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../Context/AuthProvider";
import { headerStyle, modalStyle } from "../OrderEntry/styles";
import { tokens } from "../../Common/designTokens"; // added token import
import BondCharacteristicTab from "../OrderEntry/BondCharacteristicTab";
import { useBondCharacteristicStore } from "../../Store/bondCharacteristicStore";
import { updateBondCharacteristic } from "../../http/api"; // assumes API exists
import { filterObjectByBondType } from "../../Utils/bondTypeFieldFilter";
import {
  toBackendFilterKeys,
  withUiFilterKeyAliases,
} from "../../Utils/filterKeyAliases";
import {
  maturityDateToTenorYears,
  MATURITY_TENOR_PADDING_DAYS,
} from "../../Utils/filterUtils";

const EditBondCharacteristic = ({ open, onClose, characteristicData, onSubmit }) => {
  const { token } = useAuth();
  const {
    bondCharacteristicData,
    setBondCharacteristicData,
    resetBondCharacteristicData,
    clearValidationErrors,
    setFieldErrors,
    clearFieldErrors,
    setChecked,
    checked: storeChecked,
    fieldErrors,
  } = useBondCharacteristicStore();
  const { userId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hasCharacteristicValidationErrors, setHasCharacteristicValidationErrors] = useState(false);

  // Prefill on open
  useEffect(() => {
    if (open && characteristicData) {
      resetBondCharacteristicData();

      let parsed = {};
      if (characteristicData.characteristics) {
        try {
          parsed = typeof characteristicData.characteristics === 'string'
            ? JSON.parse(characteristicData.characteristics)
            : characteristicData.characteristics;
        } catch {
          parsed = {};
        }
      }
      // The form below is keyed by the UI spelling (couponMin, ...) while the stored JSON
      // carries the backend spelling (minCoupon, ...); accept either
      parsed = withUiFilterKeyAliases(parsed);

      // Extract duration operator (support >=, <=, or trailing plus e.g. "1+")
      let durationComparison = "";
      let durationField = parsed.duration || characteristicData.duration || "";
      if (/^\d+\+$/.test(durationField)) {
        const num = durationField.replace('+','').trim();
        durationField = `>=${num}`;
        durationComparison = "greaterThanOrEqual";
      } else if (durationField?.startsWith(">=")) {
        durationComparison = "greaterThanOrEqual";
      } else if (durationField?.startsWith("<=")) {
        durationComparison = "lessThanOrEqual";
      }

      const toNum = (v) => {
        if (v === 0 || v === '0') return 0;
        if (v === '' || v === null || v === undefined) return '';
        const n = Number(v);
        return isNaN(n) ? '' : n;
      };

      // Strip trailing Y (e.g. "1Y" -> 1) for maturity range
      // paddingDays inverts the preview's own -90/+90 so an absolute date round-trips back
      // to itself: +90 for the lower bound, -90 for the upper. Only applies to dates —
      // a stored "5Y" is a plain tenor and the preview's padding is intended there.
      const stripYear = (v, paddingDays = 0) => {
        if (v === null || v === undefined || v === '') return '';
        if (typeof v === 'number') return v;
        const raw = String(v).trim();
        const s = raw.toUpperCase();
        if (/^\d+(\.\d+)?Y$/.test(s)) return Number(s.replace('Y', ''));
        if (!isNaN(Number(s))) return Number(s);
        const years = maturityDateToTenorYears(raw, paddingDays);
        return years === null ? '' : years;
      };

      const mapped = {
        side: characteristicData.side || "BUY",
        account: characteristicData.account || "",
        bondType: characteristicData.type || characteristicData.bondType || "Treasury",
        
        userId: userId || null,
        comments: characteristicData.comments || "",
        qty: toNum(characteristicData.qty || characteristicData.quantity),
        duration: durationField || "",
        durationComparison,
        couponMin: toNum(parsed.couponMin),
        couponMax: toNum(parsed.couponMax),
        // Either spelling may be stored: the form writes maturityMin/Max, the roll-over
        // job writes minMaturity/maxMaturity as already-widened absolute dates
        maturityMin: stripYear(
          parsed.maturityMin ?? parsed.minMaturity,
          MATURITY_TENOR_PADDING_DAYS
        ),
        maturityMax: stripYear(
          parsed.maturityMax ?? parsed.maxMaturity,
          -MATURITY_TENOR_PADDING_DAYS
        ),
        minimumRating: parsed.minimumRating || characteristicData.minimumRating || "",
        minimumRatingMoody: parsed.minimumRatingMoody || "",
        zeroCoupon: parsed.zeroCoupon || "",
        callable: parsed.callable || parsed.call || "",
        state: parsed.state || "",
        underlyingRating: parsed.underlyingRating || "",
        insured: parsed.insured || "",
        taxable: parsed.taxable || "",
        paymentFrequency: parsed.paymentFrequency || "",
        incomeSource: parsed.incomeSource || "",
        altMinTax: parsed.altMinTax || "",
        securityCharge: (() => {
          const sc = parsed.securityCharge || characteristicData.securityCharge;
          if (!sc) return ["ALL"];
          if (Array.isArray(sc)) return sc.length > 0 ? sc : ["ALL"];
          return [sc]; // normalize backend string e.g. "ALL" → ["ALL"]
        })(),
        sector: parsed.sector || "",
        nextCallDate: parsed.nextCallDate || "",
        priceMin: toNum(parsed.priceMin),
        priceMax: toNum(parsed.priceMax),
        yieldMin: toNum(parsed.yieldMin),
        yieldMax: toNum(parsed.yieldMax),
        issuer: parsed.issuer || "",
        characteristicId: characteristicData.characteristicId || characteristicData.id || "",
        // Keep the rung linkage on the edited characteristic. The modify endpoint
        // patches the loaded entity, so these survive server-side either way, but
        // holding them here keeps the in-flight edit state faithful to the row.
        bondLadderId: characteristicData.bondLadderId ?? null,
        bondLadderRungId: characteristicData.bondLadderRungId ?? null,
        // Ladder Name and Reason are read-only context for a rung's characteristic;
        // BondCharacteristicTab renders each only when it has a value, so a standalone
        // characteristic still shows neither
        ladderName:
          characteristicData.ladderName || characteristicData.nameOfTheLadder || "",
        reason: characteristicData.reason || "",
      };

      setBondCharacteristicData(mapped);

      // After data set, mark checkboxes for populated fields (mirrors ladder workflow)
      setTimeout(() => {
        const checks = {};
        if (mapped.couponMin !== '' || mapped.couponMax !== '') checks.couponRange = true;
        if (mapped.maturityMin !== '' || mapped.maturityMax !== '') checks.maturityRanges = true;
        if (mapped.priceMin !== '' || mapped.priceMax !== '') checks.price = true;
        if (mapped.yieldMin !== '' || mapped.yieldMax !== '') checks.yield = true;
        if (mapped.minimumRating) checks.minimumRating = true;
        if (mapped.minimumRatingMoody) checks.minimumRatingMoody = true;
        if (mapped.zeroCoupon) checks.zeroCoupon = true;
        if (mapped.callable) checks.callable = true;
        if (mapped.state) checks.state = true;
        if (mapped.underlyingRating) checks.underlyingRating = true;
        if (mapped.insured) checks.insured = true;
        if (mapped.taxable) checks.taxable = true;
        if (mapped.paymentFrequency) checks.paymentFrequency = true;
        if (mapped.incomeSource) checks.incomeSource = true;
        if (mapped.altMinTax) checks.altMinTax = true;
        if (!!(parsed.securityCharge || characteristicData.securityCharge)) checks.securityCharge = true;
        if (mapped.sector) checks.sector = true;
        if (mapped.nextCallDate) checks.nextCallDate = true;
        if (mapped.issuer) checks.issuer = true;
        if (mapped.duration) checks.duration = true;

        Object.entries(checks).forEach(([field, val]) => {
          setChecked('bondCharacteristic', field, val);
        });

        clearValidationErrors();
        clearFieldErrors();
      }, 80);
    }
  }, [open, characteristicData, resetBondCharacteristicData, setBondCharacteristicData, clearValidationErrors, clearFieldErrors, setChecked]);

  const validate = useCallback(() => {
    const errors = {};
    // Required fields
    if (!bondCharacteristicData.qty || Number(bondCharacteristicData.qty) <= 0) {
      errors.qty = true;
    }
    if (!bondCharacteristicData.account || !String(bondCharacteristicData.account).trim()) {
      errors.account = true;
    }
    if (!bondCharacteristicData.bondType || !String(bondCharacteristicData.bondType).trim()) {
      errors.bondType = true;
    }
    if (!bondCharacteristicData.side || !String(bondCharacteristicData.side).trim()) {
      errors.side = true;
    }
    setFieldErrors(errors);
    // Return false if any required errors OR downstream characteristic validation errors
    return Object.keys(errors).length === 0 && !hasCharacteristicValidationErrors;
  }, [
    bondCharacteristicData.qty,
    bondCharacteristicData.account,
    bondCharacteristicData.bondType,
    bondCharacteristicData.side,
    hasCharacteristicValidationErrors,
    setFieldErrors
  ]);

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      // Rebuild duration from comparison + numeric part (mirrors create)
      let durationOut = '';
      if (bondCharacteristicData.duration) {
        const rawNum = String(bondCharacteristicData.duration).replace(/[<>=]/g,'').trim();
        if (rawNum) {
          if (bondCharacteristicData.durationComparison === 'greaterThanOrEqual') durationOut = `>=${rawNum}`;
          else if (bondCharacteristicData.durationComparison === 'lessThanOrEqual') durationOut = `<=${rawNum}`;
          else durationOut = rawNum;
        }
      }

      const toNumberOrEmpty = (v) => (v === 0 || v === '0')
        ? 0
        : (v === '' || v === null || v === undefined
            ? ''
            : (isNaN(Number(v)) ? '' : Number(v)));

      const normalizeYesNo = (v) => (v === 'Yes' || v === 'No') ? v : (v ? 'Yes' : '');

      // Add Y suffix back for maturity range if present
      const addYearSuffix = (v) => (v === 0 || v === '0')
        ? '0Y'
        : (v !== '' && v !== null && v !== undefined ? `${v}Y` : '');

      let characteristicsFields = {
        couponMin: toNumberOrEmpty(bondCharacteristicData.couponMin),
        couponMax: toNumberOrEmpty(bondCharacteristicData.couponMax),
        maturityMin: addYearSuffix(bondCharacteristicData.maturityMin),
        maturityMax: addYearSuffix(bondCharacteristicData.maturityMax),
        minimumRating: bondCharacteristicData.minimumRating || '',
        minimumRatingMoody: bondCharacteristicData.minimumRatingMoody || '',
        zeroCoupon: normalizeYesNo(bondCharacteristicData.zeroCoupon),
        callable: normalizeYesNo(bondCharacteristicData.callable),
        duration: durationOut || '',
        state: bondCharacteristicData.state || '',
        underlyingRating: bondCharacteristicData.underlyingRating || '',
        insured: normalizeYesNo(bondCharacteristicData.insured),
        taxable: normalizeYesNo(bondCharacteristicData.taxable),
        paymentFrequency: bondCharacteristicData.paymentFrequency || '',
        incomeSource: bondCharacteristicData.incomeSource || '',
        altMinTax: normalizeYesNo(bondCharacteristicData.altMinTax),
        securityCharge: (() => {
          const sc = bondCharacteristicData.securityCharge;
          if (!sc || (Array.isArray(sc) && sc.length === 0)) return '';
          if (Array.isArray(sc)) return sc.includes("ALL") ? "ALL" : sc;
          return sc;
        })(),
        sector: bondCharacteristicData.sector || '',
        nextCallDate: bondCharacteristicData.nextCallDate || '',
        priceMin: toNumberOrEmpty(bondCharacteristicData.priceMin),
        priceMax: toNumberOrEmpty(bondCharacteristicData.priceMax),
        yieldMin: toNumberOrEmpty(bondCharacteristicData.yieldMin),
        yieldMax: toNumberOrEmpty(bondCharacteristicData.yieldMax),
        issuer: bondCharacteristicData.issuer || '',
      };
      // Enforce checkbox gating — only include fields the user checked
      const checkedFields = (storeChecked && storeChecked['bondCharacteristic']) || {};
      const getCheckboxKey = (f) => {
        if (f === 'couponMin' || f === 'couponMax') return 'couponRange';
        if (f === 'maturityMin' || f === 'maturityMax') return 'maturityRanges';
        if (f === 'priceMin' || f === 'priceMax') return 'price';
        if (f === 'yieldMin' || f === 'yieldMax') return 'yield';
        return f;
      };
      Object.keys(characteristicsFields).forEach((f) => {
        if (!checkedFields[getCheckboxKey(f)]) delete characteristicsFields[f];
      });

      // NEW: filter out fields not supported for the selected bond type
      characteristicsFields = filterObjectByBondType(bondCharacteristicData.bondType, characteristicsFields);

      // Prune empty values
      const filteredCharacteristics = Object.entries(characteristicsFields)
        .filter(([_, v]) => v !== '' && v !== null && v !== undefined)
        .reduce((o, [k, v]) => { o[k] = v; return o; }, {});

      const payload = {
        side: bondCharacteristicData.side || 'BUY',
        account: bondCharacteristicData.account,
        qty: bondCharacteristicData.qty,
        bondType: bondCharacteristicData.bondType,
        type: bondCharacteristicData.bondType,
        // pm: bondCharacteristicData.pm || '',
        // pmId: bondCharacteristicData.pmId || '',
        userId: userId || null,
        comments: bondCharacteristicData.comments || '',
        // Stored under the spelling the backend reads: this JSON is replayed through the
        // filter API later, and only minCoupon/minMaturity/... are read there
        characteristics: JSON.stringify(toBackendFilterKeys(filteredCharacteristics)),
        characteristicId: bondCharacteristicData.characteristicId
      };

      const result = await updateBondCharacteristic(token, payload);
      resetBondCharacteristicData();
      onSubmit(result);
      onClose();
    } catch (e) {
      console.error("Update characteristic failed", e);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    resetBondCharacteristicData();
    clearValidationErrors();
    clearFieldErrors();
    onClose();
  };

  // NEW: Clear All similar to ladder (preserve non-editable fields)
  const handleClearAll = () => {
    if (loading) return;
    const preserved = {
      // pm: bondCharacteristicData.pm,
      // pmId: bondCharacteristicData.pmId,
      account: bondCharacteristicData.account,
      side: bondCharacteristicData.side,
      characteristicId: bondCharacteristicData.characteristicId, // preserve characteristic id
    };
    resetBondCharacteristicData();
    setBondCharacteristicData(preserved);
    clearValidationErrors();
    clearFieldErrors();
    setHasCharacteristicValidationErrors(false);
    if (typeof window !== 'undefined') {
      window.bondValidationErrors = {};
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{
        ...modalStyle,
        width: "66vw",
        height: "90vh",
        maxHeight: 800,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}>
        <Box sx={{ ...headerStyle, flexShrink: 0 }}>
          <Typography variant="h6" sx={{ color: tokens.colors.fontColour, fontWeight: "bold" }}>
            Edit Bond Characteristic
          </Typography>
          <IconButton size="small" onClick={handleClose} sx={{ color: tokens.colors.fontColour }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ 
          flex: 1, 
          overflow: "hidden", 
          display: "flex", 
          flexDirection: "column",  // changed to ensure full-width stretching
          minHeight: 0,
          minWidth: 0               // ensure no horizontal overflow constraints
        }}>
            <BondCharacteristicTab
              readOnly="edit"
              accountIdentifiers={bondCharacteristicData.account ? [bondCharacteristicData.account] : []}
              errors={fieldErrors}          
              onValidationChange={setHasCharacteristicValidationErrors}
            />
        </Box>
        <Box sx={{
          flexShrink: 0,
          p: 2,
          borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
          backgroundColor: tokens.colors.defaultGridRowColor,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2
        }}>
          {/* Removed separate Clear All button; repurposed Cancel as Clear All */}
          <Button
            variant="outlined"
            disabled={loading}
            onClick={handleClearAll}
            sx={{ textTransform: "none", fontWeight: 700, width: 150, height: 46, borderRadius: "8px"}}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            disabled={loading || hasCharacteristicValidationErrors}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              width: 170,
              height: 46,
              borderRadius: "8px",
              backgroundColor: tokens.palette.primary,
              '&:hover': { backgroundColor: tokens.palette.primaryA80 },
              ...(hasCharacteristicValidationErrors && { opacity: 0.6, cursor: "not-allowed" })
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Update"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
  
export default EditBondCharacteristic;
